import { invariant } from "@epic-web/invariant";
import { useLocation, useParams, useSearchParams } from "react-router-dom";
import colors from "tailwindcss/colors";
import { refreshAccessToken as apiRefreshAccessToken } from "~/api/authentication/authentication";
import { useGetCurrentUser } from "~/api/users/users";
import { dayjs } from "~/utils/date";
import {
  Category,
  ChatMessage,
  DoclistTreeItem,
  DoclistTreeItemSectionsItem,
  GetBillingUsage200,
  GetBillingUsage200UsageItem,
  UserRole,
} from "./api/model";
import { SettingsMenuItem } from "./features/settings/components/types";
import { deleteCookie, getCookie, setCookie } from "./utils/cookies";

interface RouteParams {
  organizationId?: string;
  caseId?: string;
  documentId?: string;
  chatId?: string;
  settingsItem?: SettingsMenuItem;
  searchParams?: string;
}

export const ROUTES = {
  LOGIN: "/login",
  REGISTER: "/register",
  CHANGE_PASSWORD: "/change-password",
  FORGOT_PASSWORD: "/forgot-password",
  ACCESS_DENIED: "/share/access-denied",
  DEMO_CASE_GENERATION: ({ organizationId }: RouteParams) =>
    `/organizations/${organizationId}/demo-case-generation`,
  DEMO_CASE_UPLOAD: ({ organizationId, caseId }: RouteParams) =>
    `/organizations/${organizationId}/cases/${caseId}/demo-case-generation`,
  CASES: ({ organizationId }: RouteParams) =>
    `/organizations/${organizationId}/cases`,
  CASE: ({ organizationId, caseId }: RouteParams) =>
    `/organizations/${organizationId}/cases/${caseId}`,
  CHAT_DOCUMENT: ({
    organizationId,
    caseId,
    chatId,
    searchParams,
  }: RouteParams) =>
    `/organizations/${organizationId}/cases/${caseId}/chat/${chatId}${
      searchParams ? `${searchParams}` : ""
    }`,
  SETTINGS: ({ organizationId }: RouteParams) =>
    `/organizations/${organizationId}/settings`,
  SETTINGS_ITEM: ({ organizationId, settingsItem }: RouteParams) =>
    `/organizations/${organizationId}/settings/${settingsItem}`,
};

export const storeAccessToken = (token: string): void => {
  setCookie("skyAccessToken", token);
};

export const storeRefreshToken = (token: string): void => {
  setCookie("skyRefreshToken", token);
};

export const storeForgotPasswordEmail = (email: string): void => {
  setCookie("forgotPasswordEmail", email);
};

export const getStoredForgotPasswordEmail = (): string | null => {
  return getCookie("forgotPasswordEmail");
};

export const getStoredAccessToken = (): string | null => {
  return getCookie("skyAccessToken");
};

export const storeLocale = (locale: string): void => {
  setCookie("skyLocale", locale);
};

export const storeUsername = (username: string): void => {
  setCookie("skyUsername", username);
};

export const getStoredLocale = (): string | null => {
  return getCookie("skyLocale");
};

export const getStoredRefreshToken = (): string | null => {
  return getCookie("skyRefreshToken");
};

export const getStoredUsername = (): string | null => {
  return getCookie("skyUsername");
};

export const removeAllTokens = (): void => {
  deleteCookie("skyAccessToken");
  deleteCookie("skyRefreshToken");
  deleteCookie("skyUsername");
};

// Ensure only one refresh call is in-flight at a time across the app
let refreshInFlight: Promise<string | null> | null = null;

export const refreshToken = async (options?: {
  force?: boolean;
}): Promise<string | null> => {
  if (refreshInFlight && !options?.force) return refreshInFlight;

  const refreshPromise = (async () => {
    const storedRefreshToken = getStoredRefreshToken();
    const username = getStoredUsername();

    if (!storedRefreshToken || !username) {
      return null;
    }

    try {
      const data = await apiRefreshAccessToken({
        refreshToken: storedRefreshToken,
        username,
      });
      const newAccessToken = data.accessToken;
      if (newAccessToken) {
        storeAccessToken(newAccessToken);
        return newAccessToken;
      }
      return null;
    } catch (error) {
      return null;
    }
  })();

  if (!options?.force) {
    refreshInFlight = refreshPromise;
    refreshPromise.finally(() => {
      refreshInFlight = null;
    });
  }

  return refreshPromise;
};

export const getLoginUrl = (): string => {
  const pathname = window.location.pathname;
  const search = window.location.search;
  const retpathParam =
    pathname !== "/" ? `?retpath=${encodeURIComponent(pathname + search)}` : "";
  return ROUTES.LOGIN + retpathParam;
};

export const formatDate = (dateStr?: string): string => {
  if (!dateStr) {
    return "";
  }
  const date = new Date(dateStr);
  const day: string = String(date.getDate()).padStart(2, "0");
  const month: string = String(date.getMonth() + 1).padStart(2, "0");
  const year: number = date.getFullYear();

  const hours: string = String(date.getHours()).padStart(2, "0");
  const minutes: string = String(date.getMinutes()).padStart(2, "0");

  return `${month}/${day}/${year} ${hours}:${minutes}`;
};

export const formatBytes = (
  bytes: number,
  targetUnit = "MB",
  decimals = 2
): string => {
  if (bytes === 0) return "0 Bytes";
  const k = 1024;
  const sizes = ["Bytes", "KB", "MB", "GB", "TB", "PB", "EB", "ZB", "YB"];
  const targetIndex = sizes.indexOf(targetUnit);

  if (targetIndex === -1) {
    throw new Error(`Invalid target unit: ${targetUnit}`);
  }

  const value = bytes / Math.pow(k, targetIndex);
  return `${value.toFixed(decimals)} ${targetUnit}`;
};

export const useOrganizationParams = () => {
  const { organizationId } = useParams<{ organizationId: string }>();
  invariant(organizationId, "organizationId is required");
  return { organizationId };
};

export const useProjectParams = () => {
  const { organizationId, caseId } = useParams<{
    organizationId: string;
    caseId: string;
  }>();
  invariant(organizationId, "organizationId param is not provided on the page");
  invariant(caseId, "caseId param is not provided on the page");
  return { organizationId, caseId };
};

export const useChatDocumentParams = () => {
  const { organizationId, caseId, chatId } = useParams<{
    organizationId: string;
    caseId: string;
    chatId: string;
  }>();

  const [searchParams] = useSearchParams();
  const documentId = searchParams.get("documentId");
  const { data: currentUser } = useGetCurrentUser();

  if (!organizationId || !caseId || !chatId) {
    throw new Error("Missing required parameters");
  }

  const isViewOnly = !!documentId;
  const isGuest = currentUser?.role === UserRole.GUEST;
  const isRestricted = isViewOnly || isGuest;

  return {
    organizationId,
    caseId,
    chatId,
    documentId,
    isViewOnly,
    isGuest,
    isRestricted,
  };
};

export const useSettingsParams = (): {
  organizationId: string;
  settingsItem: SettingsMenuItem;
} => {
  const { organizationId } = useParams<{
    organizationId: string;
  }>();
  const location = useLocation();
  const settingsItem = location.pathname.split("/")[4] as SettingsMenuItem;
  invariant(organizationId, "organizationId is not provided on the page");
  return { organizationId, settingsItem };
};

export function downloadBlob(blob: Blob, fileName: string) {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

export function downloadFile(url: string, fileName: string) {
  fetch(url)
    .then((response) => response.blob())
    .then((blob) => {
      downloadBlob(blob, fileName);
    });
}

export const generateRouteByCaseId = (
  organizationId: string,
  caseId: string
): string => {
  return `/organizations/${organizationId}/cases/${caseId}`;
};

export const getCapitalizedStr = (str: string): string => {
  return str.charAt(0).toUpperCase() + str.slice(1);
};

export function assertUnreachable(
  _v: "error: Did you forget to handle this type?"
): never {
  throw new Error("Didn't expect to get here");
}

export function getOccupiedColors(categories: Category[]) {
  return categories.map((c) => c.color.toLowerCase());
}

type ColorName = keyof typeof colors;
type ColorShade = "300" | "400" | "500" | "600" | "700" | "800" | "900";

export function getVacantCategoryColor(occupiedColors: string[]): string {
  const colorNames: ColorName[] = [
    "slate",
    "gray",
    "zinc",
    "neutral",
    "stone",
    "red",
    "orange",
    "amber",
    "yellow",
    "lime",
    "green",
    "emerald",
    "teal",
    "cyan",
    "sky",
    "blue",
    "indigo",
    "violet",
    "purple",
    "fuchsia",
    "pink",
    "rose",
  ];
  const colorShades: ColorShade[] = [
    "300",
    "400",
    "500",
    "600",
    "700",
    "800",
    "900",
  ];
  const availableColors = colorNames
    .flatMap((name) => colorShades.map((shade) => colors[name][shade]))
    .filter((color) => !occupiedColors.includes(color.toLowerCase()));

  if (availableColors.length === 0) {
    throw new Error("All available colors are already in use.");
  }

  return availableColors[Math.floor(Math.random() * availableColors.length)];
}

export const generateChatMarkdownContent = ({
  title,
  messages,
  date,
}: {
  title: string;
  messages: ChatMessage[];
  date: string;
}) => {
  const markdownMessages = messages
    .map(
      (message) =>
        `**${message.role === "user" ? "User" : "SKY"}:** ${message.content} (${formatDate(new Date(message.timestamp).toISOString())})`
    )
    .join("\n\n");

  return `# ${title}

${markdownMessages}

*Chat export date: ${date}*`;
};

export const formatDateForAPI = (dateStr: string): string => {
  if (!dateStr) return "";
  const [month, day, year] = dateStr.split("/");
  return dayjs(`${year}-${month}-${day}`).format("YYYY-MM-DD");
};

export const extractDateFromName = (name: string): string | null => {
  const dateRegex = /(\w+\s+\d{1,2},?\s+\d{4})/;
  const match = name.match(dateRegex);

  if (match) {
    const dateString = match[1];
    const parsedDate = dayjs(dateString, ["MMMM D, YYYY", "MMMM D YYYY"], true);

    if (parsedDate.isValid()) {
      return parsedDate.format("YYYY-MM-DD");
    }
  }

  return null;
};

export function processUsageData(data: GetBillingUsage200, from: string) {
  const daily_usage: Record<string, number> = {};

  const firstDate = dayjs(data.usage.length ? data.usage[0]?.createdAt : from);
  const year = firstDate.year();
  const month = firstDate.month() + 1;
  const currentDate = dayjs();
  const daysInMonth = dayjs(`${year}-${month}`).daysInMonth();
  const lastDay =
    currentDate.year() === year && currentDate.month() + 1 === month
      ? currentDate.date()
      : daysInMonth;

  for (let i = 1; i <= lastDay; i++) {
    daily_usage[i.toString()] = 0;
  }

  data.usage.forEach((item: GetBillingUsage200UsageItem) => {
    const date = dayjs(item.createdAt);
    if (date.year() === year && date.month() === month - 1) {
      const day = date.date().toString();
      daily_usage[day] += item.quantity ?? 0;
    }
  });

  const chart_data = Object.entries(daily_usage)
    .map(([date, pages]) => ({ date, pages }))
    .sort((a, b) => dayjs(a.date).diff(dayjs(b.date)));

  return chart_data;
}

export function findPreviousSectionId(
  items: DoclistTreeItem[],
  currentCategoryId: string,
  currentIndex: number
): string | null {
  const flattedSections = items.flatMap((item) => item.sections);

  let currentSectionIndex = -1;
  let count = 0;

  for (const item of items) {
    if (String(item.category.id) === currentCategoryId) {
      currentSectionIndex = count + currentIndex;
      break;
    }
    count += item.sections.length;
  }

  if (currentSectionIndex <= 0) {
    return null;
  }

  return flattedSections[currentSectionIndex - 1].id;
}

export const removeEmptySectionAndCategory = (
  items: DoclistTreeItem[],
  sourceSection: DoclistTreeItemSectionsItem
) => {
  if (sourceSection.pageCount === 0) {
    // Find the category containing this section
    const categoryWithSection = items.find((item) =>
      item.sections.some((section) => section.id === sourceSection.id)
    );

    if (categoryWithSection) {
      // Delete the empty section from the category
      categoryWithSection.sections = categoryWithSection.sections.filter(
        (section) => section.id !== sourceSection.id
      );
      // If there are no sections left in the category, delete the category
      if (categoryWithSection.sections.length === 0) {
        const categoryIndex = items.findIndex(
          (item) => item.categoryId === categoryWithSection.categoryId
        );
        if (categoryIndex !== -1) {
          items.splice(categoryIndex, 1);
        }
      }
    }
  }
};

export const isCategorySelected = (
  items: DoclistTreeItem[],
  categoryId: number,
  exportSectionIds: string[]
) => {
  const sectionIds =
    items
      ?.find((item) => item.category.id === categoryId)
      ?.sections.map((section) => section.id) || [];
  return sectionIds.some((id) => exportSectionIds.includes(id));
};

export const getCategoryBackground = (
  items: DoclistTreeItem[],
  categoryId: number,
  color: string,
  isExportMode: boolean,
  exportSectionIds: string[],
  activeCategoryId: number | undefined,
  editedCategoryId: number | null,
  isHovered: boolean,
  searchTerm: string
) => {
  if (isExportMode) {
    return isCategorySelected(items, categoryId, exportSectionIds)
      ? `linear-gradient(90deg, rgba(255, 255, 255, 0) 5.24%, rgba(255, 255, 255, 0.25) 93.21%), ${color}`
      : `${color}40`;
  }
  return [activeCategoryId, editedCategoryId].includes(categoryId) ||
    searchTerm ||
    isHovered
    ? `linear-gradient(90deg, rgba(255, 255, 255, 0) 5.24%, rgba(255, 255, 255, 0.25) 93.21%), ${color}`
    : `${color}40`;
};

export const EMAIL_REGEX = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

export const validateEmail = (email: string) => {
  return EMAIL_REGEX.test(email);
};

// Temporary: block public email providers during signup to test lead quality
export const REQUEST_DEMO_URL = "https://usesky.ai/request-demo";

// Top public/free email providers (can be adjusted)
export const POPULAR_EMAIL_DOMAINS: readonly string[] = [
  "gmail.com",
  "yahoo.com",
  "outlook.com",
  "hotmail.com",
  "live.com",
  "aol.com",
  "icloud.com",
  "me.com",
  "mac.com",
  "msn.com",
  "mail.com",
  "protonmail.com",
  "yandex.com",
  "yandex.ru",
  "zoho.com",
  "gmx.com",
  "gmx.de",
  "mail.ru",
  "qq.com",
  "163.com",
] as const;

export const isCustomEmailDomain = (email: string): boolean => {
  if (!validateEmail(email)) return false;
  const domain = email.split("@")[1]?.toLowerCase() ?? "";
  return domain.length > 0 && !POPULAR_EMAIL_DOMAINS.includes(domain);
};
